import { useCallback, useEffect, useRef, useState } from "react"

/**
 * Minimal client-side data hook: runs `fn` when `deps` change, ignores stale
 * results, never runs during SSR. `initial` seeds the result (e.g. from the
 * server-rendered pageContext) so the first client render matches the HTML;
 * the fetch still runs on mount (the old SPA always loaded client-side, and
 * product {load} records view history) and replaces the seed when it resolves.
 */
export default function useAsync(fn, deps, { initial = null, enabled = true } = {}) {
  // `loaded` is about this browser having asked, not about there being a
  // result: a seed from the server is a result nobody here asked for, and
  // anything that depends on who is asking has to wait for the real answer.
  const [state, setState] = useState({ result: initial, pending: !initial && enabled, error: null, loaded: false })
  const runId = useRef(0)

  const run = useCallback(() => {
    const id = ++runId.current
    setState((s) => ({ ...s, pending: true, error: null }))
    Promise.resolve()
      .then(fn)
      .then((result) => {
        if (id !== runId.current) return
        setState({ result, pending: false, error: null, loaded: true })
      })
      .catch((error) => {
        if (id !== runId.current) return
        setState((s) => ({ result: s.result, pending: false, error, loaded: true }))
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  useEffect(() => {
    if (!enabled) return
    run()
  }, [run, enabled])

  return { ...state, refresh: run }
}
